import { useState } from "react";

import { formatDateForPanelDisplay } from "@/Utils/helper";
import { EventTypeIcon } from "@/Utils/litigationDateLayout";

import UploadIcon from "../../../../public/bp_assets/img/cloud_icon.svg";
import "../../LitigationDashboard/LitigationActSummaryTable.css";
import "./LitigationDocumentMenu.css";

const getEventTypeName = (panel) =>
  !panel?.event_type_id
    ? "Statute of Limitations"
    : panel.event_type_id.litigation_event_type || "";

function LitigationEventDates({ panel }) {
  const renderDate = (date) => {
    const [, timePart] = String(date).split("T");
    let time = "";
    if (timePart && !panel.is_allday) {
      const [hours = "00", minutes = "00"] = timePart.split(":");
      const hour = parseInt(hours, 10);
      time = `${hour % 12 || 12}:${minutes.padStart(2, "0")} ${hour >= 12 ? "pm" : "am"}`;
    }

    return (
      <span className="litigation-summary-event-date">
        <span>{formatDateForPanelDisplay(date)}</span>
        {!panel.is_allday && <span>{time}</span>}
      </span>
    );
  };

  const renderEndpoint = (endpoint) => {
    if (panel[endpoint]) return renderDate(panel[endpoint]);

    const isStart = endpoint === "start_date";
    const usesDate = isStart ? panel.uses_start_date : panel.uses_end_date;
    const usesDependentLabel = isStart ? panel.is_dependant_date : !panel.is_dependant_date;
    const label = usesDate
      ? panel[isStart ? "start_date_label" : "end_date_label"] ||
        (usesDependentLabel
          ? panel.dependant_date_type?.dependent_date_name || "Dependant Date"
          : isStart
            ? "Start Date"
            : "End Date")
      : "";

    return <span className="text-primary-25 fw-semibold text-capitalize p-r-5">{label}</span>;
  };

  if (!panel.event_type_id) return renderEndpoint("end_date");
  if (panel.uses_start_date && panel.uses_end_date) {
    return (
      <>
        {renderEndpoint("start_date")}
        <span className="litigation-summary-event-date-range-end">
          <span className="text-primary-25">to</span>
          {renderEndpoint("end_date")}
        </span>
      </>
    );
  }
  if (panel.uses_start_date) return renderEndpoint("start_date");
  if (panel.uses_end_date) return renderEndpoint("end_date");
  return null;
}

export function LitigationEventHeading({
  panel,
  index,
  showNumber = true,
  showStatus = true,
  singleLine = false,
  textColorClass = "text-black",
}) {
  const statusLabel = panel.completed ? "Completed" : panel.active ? "Active" : "Disabled";
  const statusColor = panel.completed
    ? "var(--primary)"
    : panel.active
      ? "green"
      : "var(--primary-25)";

  return (
    <div
      className={`litigation-document-event-row ${singleLine ? "litigation-document-event-row--single-line " : ""}${!panel.completed && !panel.active ? "text-primary-25" : textColorClass}`}
    >
      {showNumber && <span className="litigation-document-event-number">{index + 1}</span>}
      <div
        className={`litigation-summary-event-heading${showStatus ? "" : " litigation-summary-event-heading--without-status"}`}
      >
        {showStatus && (
          <span
            aria-label={statusLabel}
            className="litigation-summary-status-icon"
            role="img"
            style={{ backgroundColor: statusColor }}
            title={statusLabel}
          />
        )}
        <span className="litigation-summary-event-icon">
          <EventTypeIcon name={getEventTypeName(panel)} />
        </span>
        <span className="litigation-summary-event-content">
          <span className="litigation-summary-event-dates">
            <LitigationEventDates panel={panel} />
          </span>
          <span className="litigation-summary-event-type text-uppercase">
            {getEventTypeName(panel)}
            {": "}
            <span className="text-capitalize fw-normal">
              {panel.discovery_type?.name}
              {panel.filing_type?.name}
              {panel.name}
            </span>
          </span>
        </span>
      </div>
    </div>
  );
}

export default function LitigationDocumentMenu({
  page,
  panels = [],
  inactivePanels = [],
  slots,
  showMenuBar,
  setShowMenuBar,
  onAttach,
}) {
  const [showInactive, setShowInactive] = useState(false);
  const [hoveredPanel, setHoveredPanel] = useState(null);
  const pageSlot = slots.find((slot) => slot.slot_number === 0);

  const renderPanels = (events, listName) =>
    events.length === 0 ? (
      <li className="litigation-document-empty fw-normal text-primary-25">
        {listName === "inactive"
          ? "No inactive litigation events"
          : "No upcoming litigation events"}
      </li>
    ) : (
      events.map((panel, index) => {
        const hoverKey = `${listName}-${index}`;
        return (
          <li
            key={panel.id ?? index}
            className="litigation-document-event-entry"
            onMouseEnter={() => setHoveredPanel(hoverKey)}
            onMouseLeave={() => setHoveredPanel(null)}
          >
            <span
              className="litigation-document-menu-arrow"
              style={{ visibility: hoveredPanel === hoverKey ? "visible" : "hidden" }}
            />
            <LitigationEventHeading panel={panel} index={index} />
            <ul
              className="document-pop-up-sidebar-menubar litigation-document-slots"
              style={{ display: hoveredPanel === hoverKey ? "block" : "none" }}
            >
              <li className="litigation-document-slot-heading text-black">
                <EventTypeIcon name={getEventTypeName(panel)} />
                <LitigationEventDates panel={panel} />
                <span>{getEventTypeName(panel)}</span>
              </li>
              {slots
                .filter((slot) => slot.slot_number !== 0)
                .map((slot) => {
                  const docsInSlot = (panel.docs || []).filter(
                    (doc) => doc.document_slot?.slot_number === slot.slot_number
                  );
                  return (
                    <li
                      key={slot.id}
                      className="litigation-document-slot-entry"
                      style={{ cursor: docsInSlot.length > 0 ? "default" : "pointer" }}
                      onClick={() => onAttach(slot, panel)}
                    >
                      {docsInSlot.length > 0 ? (
                        <i className="ic ic-file-colored ic-15 cursor-pointer" />
                      ) : (
                        <img src={UploadIcon} className="ic-15 cursor-pointer" alt="" />
                      )}
                      <span
                        className={`ms-2 fw-bold ${docsInSlot.length === 0 ? "hoverable-text text-primary-25" : ""}`}
                      >
                        {slot.slot_number}.
                      </span>
                      <span
                        className={`ms-2 fw-bold ${docsInSlot.length === 0 ? "hoverable-text text-primary-25" : ""}`}
                      >
                        {docsInSlot.length > 0
                          ? docsInSlot[0].file_name
                          : slot.slot_name || "Available"}
                      </span>
                    </li>
                  );
                })}
            </ul>
          </li>
        );
      })
    );

  return (
    <li
      className="litigation-document-page"
      onMouseEnter={() => setShowMenuBar(page.id)}
      onMouseLeave={() => {
        setShowMenuBar(null);
        setShowInactive(false);
        setHoveredPanel(null);
      }}
    >
      <a className="cursor-pointer d-flex align-items-center litigation-document-page-label">
        <img src={page.page_icon ?? ""} className="ic-15" alt="" />
        {page.name}
        <span className="caret" />
      </a>
      <ul
        className="document-pop-up-sidebar-menubar litigation-document-menu"
        style={{ display: showMenuBar === page.id ? "block" : "none" }}
      >
        <li className="litigation-document-page-header">
          {pageSlot && (
            <button
              type="button"
              className="litigation-document-page-button background-white-hover-primary-four"
              onClick={() => onAttach(pageSlot)}
            >
              <img src={page.page_icon ?? ""} alt="" />
              <span>Attach to {page.name} Page</span>
              <span className="litigation-document-menu-arrow" />
            </button>
          )}
          <div
            className="litigation-document-inactive-entry"
            onMouseEnter={() => {
              setShowInactive(true);
              setHoveredPanel(null);
            }}
            onMouseLeave={() => {
              setShowInactive(false);
              setHoveredPanel(null);
            }}
          >
            <span className="fw-normal">Attach Docs to Inactive Events from the Event.</span>
            <ul
              className="document-pop-up-sidebar-menubar litigation-document-inactive-menu"
              style={{ display: showInactive ? "block" : "none" }}
            >
              {renderPanels(inactivePanels, "inactive")}
            </ul>
          </div>
        </li>
        <li className="litigation-document-list-heading bg-primary-5">{page.panel_name}</li>
        {renderPanels(panels, "upcoming")}
      </ul>
    </li>
  );
}
